import { useMemo, useCallback } from 'react';

import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid2';
import { Card, Stack, Typography, CircularProgress } from '@mui/material';

import { emailStep1Data } from 'src/utils/constants';

import { useTranslate } from 'src/locales';
import { useChannelContext } from 'src/pages/channel/hooks';
import { ChannelStepLayout } from 'src/pages/channel/shared';
import { useChannelWizardAccessContext } from 'src/pages/channel/hooks/use-channel-wizard-access-context';
import { isEmailProviderAllowedForRole } from 'src/pages/setting/components/channel-feature-flags/utils/channel-create-family-eligibility';

import { Image } from 'src/components/image';

export function EmailStep2() {
  const { t } = useTranslate('translation');
  const { state: channelState, onSelectEmailProvider, onChangeStep } = useChannelContext();
  const {
    tenantFlags,
    wizardAccessReady,
    showWizardAccessLoader,
    roleRowsForEligibility,
    isSuperAdmin,
  } = useChannelWizardAccessContext();

  const filteredProviders = useMemo(() => {
    const all = emailStep1Data(t);
    if (!wizardAccessReady) return [];
    return all.filter((item) =>
      isEmailProviderAllowedForRole(item.value, tenantFlags, roleRowsForEligibility, isSuperAdmin)
    );
  }, [t, wizardAccessReady, tenantFlags, roleRowsForEligibility, isSuperAdmin]);

  const handleDoubleClick = useCallback(
    (value: string) => {
      onSelectEmailProvider(value);
      onChangeStep('next');
    },
    [onSelectEmailProvider, onChangeStep]
  );

  return (
    <ChannelStepLayout title="" subtitle="" isNextDisabled={!channelState.emailProviderType}>
      <Box sx={{ height: '90%', display: 'flex', flexDirection: 'column' }}>
        {showWizardAccessLoader ? (
          <Stack alignItems="center" justifyContent="center" minHeight={240}>
            <CircularProgress size={36} />
          </Stack>
        ) : filteredProviders.length === 0 ? (
          <Typography variant="body2" color="text.secondary" textAlign="center" sx={{ py: 4 }}>
            {t('NO_CHANNELS_FROM_TENANT_FLAGS')}
          </Typography>
        ) : (
          <Grid container spacing={3} justifyContent="space-between">
            {filteredProviders.map((data) => (
              <Grid key={data.value} size={{ xs: 12, md: 6 }}>
                <Card
                  sx={{
                    p: 4,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 2,
                    margin: '0 auto',
                    cursor: 'pointer',
                    width: '90%',
                    height: '150px',
                    border:
                      channelState.emailProviderType === data?.value ? '1px solid #00A76F' : 'none',
                  }}
                  onClick={() => onSelectEmailProvider(data?.value)}
                  onDoubleClick={() => handleDoubleClick(data.value)}
                >
                  <Box sx={{ display: 'flex' }}>
                    <Image src={data.icon} alt={data.icon} sx={{ width: '75px', height: '75px' }} />
                  </Box>
                  <Typography variant="h6">{data.channel_name}</Typography>
                </Card>
              </Grid>
            ))}
          </Grid>
        )}
      </Box>
    </ChannelStepLayout>
  );
}
